import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  Mail,
  CalendarDays,
  ListTodo,
  FileText,
  MessageSquare,
  Search,
  Receipt,
  Workflow,
  Sparkles,
  ArrowRight,
  Shield,
  Zap,
  Globe,
} from 'lucide-react';

const FEATURES = [
  { icon: Mail, label: 'Email', desc: 'Unified inbox with AI summaries' },
  { icon: CalendarDays, label: 'Calendar', desc: 'Smart scheduling & meeting briefs' },
  { icon: ListTodo, label: 'Tasks', desc: 'AI-powered task management' },
  { icon: FileText, label: 'Documents', desc: 'Intelligent document editing' },
  { icon: MessageSquare, label: 'Chat', desc: 'AI assistant with RAG search' },
  { icon: Search, label: 'Search', desc: 'Internal + web search synthesis' },
  { icon: Receipt, label: 'Finance', desc: 'Invoice parsing & spend analysis' },
  { icon: Workflow, label: 'Workflows', desc: 'Automated approval workflows' },
];

const HIGHLIGHTS = [
  { icon: Sparkles, title: '14 AI Agents', desc: 'Purpose-built agents for every feature powered by Kaycoo Agentic Platform' },
  { icon: Shield, title: 'Enterprise Security', desc: 'Multi-tenant isolation, RLS policies, and confidential content routing' },
  { icon: Zap, title: 'Real-time Streaming', desc: 'AI responses stream instantly — no waiting for full generation' },
  { icon: Globe, title: 'Multilingual', desc: 'Full support for Finnish, English, and Swedish' },
];

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      setError(authError.message);
      setLoading(false);
      return;
    }

    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#003C41]">
      {/* Nav */}
      <header className="flex items-center justify-between px-6 py-4 md:px-12">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10">
            <Sparkles className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <span className="text-lg font-bold tracking-wide text-white">KAYCOO</span>
            <span className="ml-2 text-xs font-medium uppercase tracking-widest text-emerald-300/80">
              Office
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowLogin(true)}
          className="rounded-lg border border-white/20 px-5 py-2 text-sm font-medium text-white transition-all hover:border-white/40 hover:bg-white/5"
        >
          Sign in
        </button>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pb-16 pt-12 text-center md:px-12 md:pt-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-1.5 text-xs font-medium text-emerald-300">
          <Sparkles className="h-3.5 w-3.5" />
          Powered by Kaycoo Agentic Platform
        </div>

        <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white md:text-6xl">
          Your entire office,
          <br />
          <span className="bg-gradient-to-r from-emerald-300 to-teal-200 bg-clip-text text-transparent">
            one intelligent platform
          </span>
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
          KOIP replaces email, calendar, tasks, documents, and file storage with one AI-powered workspace.
          14 specialized agents work across every feature — summarizing emails, drafting agendas,
          editing documents, and tracking your work.
        </p>

        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            onClick={() => setShowLogin(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-400 px-6 py-3 text-sm font-semibold text-[#003C41] transition-all hover:bg-emerald-300"
          >
            Get Started <ArrowRight className="h-4 w-4" />
          </button>
          <a
            href="#features"
            className="rounded-lg border border-white/20 px-6 py-3 text-sm font-medium text-white transition-all hover:border-white/40 hover:bg-white/5"
          >
            Explore Features
          </a>
        </div>
      </section>

      {/* Feature Grid */}
      <section id="features" className="mx-auto max-w-5xl px-6 pb-16 md:px-12">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div
              key={f.label}
              className="group rounded-xl border border-white/10 bg-white/5 p-5 transition-all hover:border-emerald-400/30 hover:bg-white/[0.07]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-300 transition-colors group-hover:bg-emerald-400/20">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-white">{f.label}</h3>
              <p className="mt-1 text-xs leading-relaxed text-white/50">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="border-t border-white/10 bg-[#002A2E]">
        <div className="mx-auto max-w-5xl px-6 py-16 md:px-12">
          <h2 className="text-center text-2xl font-bold text-white md:text-3xl">
            Built for <span className="text-emerald-300">Kaycoo</span> teams
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-white/50">
            Enterprise-grade security with the simplicity of a consumer app.
            Every AI feature is purpose-built for how your team actually works.
          </p>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-300">
                  <h.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-sm font-semibold text-white">{h.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/50">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#002A2E]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-6 md:px-12">
          <span className="text-xs text-white/30">Kaycoo Group Oy — koip-test.kaycoo.ai</span>
          <span className="text-xs text-white/30">Powered by Kaycoo Agentic Platform</span>
        </div>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#003C41] p-8 shadow-2xl">
            <div className="space-y-2 text-center">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-300" />
                <h1 className="text-xl font-bold tracking-tight text-white">KAYCOO</h1>
              </div>
              <p className="text-xs font-medium uppercase tracking-widest text-emerald-300/70">
                Office Intelligence Platform
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {error && (
                <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-300">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-white/80">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="flex h-11 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/30 focus:border-emerald-400/50 focus:outline-none focus:ring-2 focus:ring-emerald-400/20"
                  placeholder="you@kaycoo.fi"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-white/80">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="flex h-11 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/30 focus:border-emerald-400/50 focus:outline-none focus:ring-2 focus:ring-emerald-400/20"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-emerald-400 text-sm font-semibold text-[#003C41] transition-colors hover:bg-emerald-300 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <button
              onClick={() => setShowLogin(false)}
              className="mt-4 w-full text-center text-xs text-white/40 hover:text-white/60"
            >
              Back to landing page
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
